import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { logApiError } from '@/lib/errors';

export function useDocuments(claimId: number, enabled = true) {
    const queryClient = useQueryClient();

    const { data: response, isLoading } = useQuery({
        queryKey: ['documents', claimId],
        queryFn: async () => {
            return await apiClient.documents.getDocuments({ claimId, limit: 100 });
        },
        enabled: !!claimId && enabled,
    });

    const uploadDocumentMutation = useMutation({
        mutationFn: async ({ file, kind = 'insurer', notify = true, folderId }: { file: File; kind?: 'insurer' | 'contractor'; notify?: boolean; folderId?: number | null }) => {
            return new Promise<import('@/api/generated/client/models').Document>((resolve, reject) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = async () => {
                    try {
                        const base64String = (reader.result as string).split(',')[1];
                        const result = await apiClient.documents.createDocument({
                            createDocumentRequest: {
                                claimId,
                                kind,
                                filename: file.name,
                                mimeType: file.type as import('@/api/generated/client/models/CreateDocumentRequest').CreateDocumentRequestMimeTypeEnum,
                                sizeBytes: file.size,
                                fileContent: base64String,
                                parseStatus: 'pending',
                                notify,
                                ...(folderId != null ? { folderId } : {}),
                            }
                        });
                        resolve(result);
                    } catch (error) {
                        reject(error);
                    }
                };
                reader.onerror = (error) => reject(error);
            });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['documents', claimId] });
            // Invalidate notifications to show new activity in bell
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    });

    const documents = response?.items || [];

    const getDocumentBlob = async (documentId: number) => {
        try {
            return await apiClient.documents.getDocumentContent({ documentId });
        } catch (error) {
            logApiError(error, 'Get document blob');
            throw error;
        }
    };

    const downloadDocument = async (documentId: number, filename: string) => {
        try {
            const blob = await getDocumentBlob(documentId);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        } catch (error) {
            logApiError(error, 'Download document');
            throw error; // Re-throw so caller can handle (e.g., show toast)
        }
    };

    const deleteDocumentMutation = useMutation({
        mutationFn: async (documentId: number) => {
            await apiClient.documents.deleteDocument({ documentId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['documents', claimId] });
            // Invalidate notifications to show new activity in bell
            queryClient.invalidateQueries({ queryKey: ['notifications'] });
        }
    });

    return {
        documents,
        isLoading,
        uploadDocument: uploadDocumentMutation.mutate,
        uploadDocumentAsync: uploadDocumentMutation.mutateAsync,
        isUploading: uploadDocumentMutation.isPending,
        getDocumentBlob,
        downloadDocument,
        deleteDocument: deleteDocumentMutation.mutate,
        isDeleting: deleteDocumentMutation.isPending
    };
}
